import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams, useNavigate } from "react-router-dom";
import { modules, formats } from "../../utils/quillModules";
import { API_URL } from "../../utils/Url";
import { Header } from "../header/Header";
import { Sidebar } from "../sidebar/Sidebar";
import { toast } from "react-toastify";
import "./Blogs.css"; // Ensure this file has the updated CSS below

const UpdateBlogs = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const token = sessionStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaKeyword, setMetaKeyword] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [author, setAuthor] = useState("");

  /* Fetch single blog details */
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/blog/get_single/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const b = data.blog;
        setTitle(b.title);
        setContent(b.description);
        setImageUrl(b.blogImage?.url || b.blogImage);
        setMetaTitle(b.metaTitle);
        setMetaKeyword(b.metaKeyword);
        setMetaDescription(b.metaDescription);
        setAuthor(b.authorName);
      } catch (err) {
        toast.error("Failed to load blog database configurations.");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchBlog();
  }, [slug, token]);

  const handleUpdate = async () => {
    if (submitting) return;

    if (!token) {
      toast.error("Unauthorized. Please login again.");
      return;
    }

    if (!title || !content || content === "<p><br></p>") {
      toast.error("Title & Description are required");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("description", content);
      formData.append("metaTitle", metaTitle);
      formData.append("metaKeyword", metaKeyword);
      formData.append("metaDescription", metaDescription);
      formData.append("authorName", author);

      if (imageFile) {
        formData.append("blogImage", imageFile);
      }

      await axios.put(`${API_URL}/api/blog/update_blog/${slug}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Blog updated successfully ✅");
      navigate("/blogs");
    } catch (err) {
      console.error("UPDATE ERROR:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to update blog ❌");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner-element" />
      </div>
    );
  }

  return (
    <div className="admin-layout-wrapper">
      {/* Top Header */}
      <Header />

      <div className="admin-main-container">
        {/* Navigation Sidebar */}
        <Sidebar />

        {/* Main Workspace Frame */}
        <div className="blogs-create-page-content">
          <div className="blogs-create-card">
            <div className="card-header">
              <h2 className="page-title">✏️ Update Blog Post</h2>
              <p className="page-subtitle">
                Modify content, replace featured graphics, and keep metadata
                optimized.
              </p>
            </div>

            <div className="form-body-layout">
              {/* Left Column: Input and Core Content Editor */}
              <div className="form-section-main">
                <div className="form-group">
                  <label>Blog Title</label>
                  <input
                    type="text"
                    className="custom-input title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Modify blog title..."
                  />
                </div>

                <div className="form-group quill-container">
                  <label>Blog Description</label>
                  <ReactQuill
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    className="quill-editor"
                  />
                </div>
              </div>

              {/* Right Column: Asset Upgrades & SEO Configurations */}
              <div className="form-section-sidebar">
                <div className="form-group">
                  <label>Featured Image</label>
                  <div className="image-upload-wrapper">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      id="blog-image-update-input"
                      onChange={(e) => setImageFile(e.target.files[0])}
                      hidden
                    />
                    <label
                      htmlFor="blog-image-update-input"
                      className="upload-dropzone"
                    >
                      {imageFile || imageUrl ? (
                        <img
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : imageUrl
                          }
                          alt="Preview"
                          className="image-preview"
                        />
                      ) : (
                        <div className="upload-placeholder">
                          <span className="upload-icon">📸</span>
                          <span>Upload new replacement image</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Author Name</label>
                  <input
                    type="text"
                    className="custom-input"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Author identity"
                  />
                </div>

                <div className="seo-box-container">
                  <h3 className="seo-title">🔍 SEO Meta Configurations</h3>

                  <div className="form-group">
                    <label>Meta Title</label>
                    <input
                      className="custom-input"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="Search engine engine tag reference"
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Keywords</label>
                    <input
                      className="custom-input"
                      value={metaKeyword}
                      onChange={(e) => setMetaKeyword(e.target.value)}
                      placeholder="comma, separated, tags"
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Description</label>
                    <textarea
                      className="custom-input custom-textarea"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      placeholder="Short engine crawler snippet text description..."
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Panel Actions */}
            <div className="form-actions-bar">
              <button className="btn-cancel" onClick={() => navigate("/blogs")}>
                Cancel Changes
              </button>
              <button
                className="btn-submit"
                disabled={submitting}
                onClick={handleUpdate}
              >
                {submitting ? "Updating Database..." : "Save & Update Post"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateBlogs;
